// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ComputedRef, ref, shallowRef } from "vue";

// TMI - content type and query definitions
import { Product } from "@tmi/types/entities";

// Authentication and Authorization services & mapping for adjusting the "slug" for the rebranded programs
import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";

import { cache, CacheClosedError, products } from "@/services/cache";
import { clientStateManager } from "@/services/error-services";
import { loadProductSeedData } from "@/services/seed-service";
import { createInitLatch } from "@/stores/init-latch";
import { createCatalogIndex, markRawRows } from "@/stores/catalog-index";
import { refreshProductsFromServer } from "@/services/data/sync/product-sync-coordinator";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

// Rank 11: a read-only catalog of raw rows, always replaced by assignment; hydration resolves each item's products.
const allProducts = shallowRef< Product[] >( [] );
const productsById = createCatalogIndex( allProducts, ( product: Product ) => product.id );
const productsBySlug = createCatalogIndex( allProducts, ( product: Product ) => product.slug );

// cache a separate list of the products that are marked as "display-able"
const displayableProducts = computed< Product[] >( () => allProducts.value.filter( ( product: Product ) => product.isDisplay ) );

const initialized = computed< boolean >( () => allProducts.value.length > 0 );
const isLoading = computed( () => loading.value );
const loading = ref< boolean >( false );
// UX roadmap #83: true once an initializeAsync() has finished, from the cache, the seed or a failure, so the brochure can
// tell "not loaded yet" (skeleton) from "no such product" (404). Left false on the quiet CacheClosedError stop.
const settled = ref< boolean >( false );
// UX roadmap #83: the brochure refetched the whole catalog on every visit and assigned whatever came back, so a failed
// request (goFetch resolves most failures as an empty result) emptied the catalog the header and pages read. The
// refresh now runs once per catalog (reset() re-arms it), in the background, and only a complete, non-empty result
// replaces what is shown. The generation drops a refresh that a reset() overtook.
const refreshing = ref< boolean >( false );
let serverRefresh: Promise< void > | null = null;
let serverRefreshLanded: boolean = false;
let catalogGeneration: number = 0;
// tmi-7be: a cache failure stops initialization until reset(), so no watcher can re-run it in a loop.
const initLatch = createInitLatch();

export const useProductsStore = defineStore( 'products', () => {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();

    function reset(): void {
        allProducts.value = [];
        productsById.clear();
        productsBySlug.clear();
        loading.value = false;
        settled.value = false;
        refreshing.value = false;
        serverRefresh = null;
        serverRefreshLanded = false;
        catalogGeneration += 1;
        initLatch.clear();
    }

    async function initializeAsync(): Promise< void > {
        if ( initialized.value || loading.value || initLatch.isHalted() ) {
            logger.debug( '[ Products ].initialize() exiting - already processing...' );
            return;
        }

        loading.value = true;

        try {
            const t0: number = performance.now();

            // Wait for the database to open before the first read.
            await cache.ready();

            const uniqueProducts = new Map< string, Product >();
            if ( !await products.isEmpty() ) {
                for ( const product of markRawRows( await products.all() ) ) {
                    uniqueProducts.set( product.id, product );
                }

                const cached: Product[] = [ ...( Array.from( uniqueProducts.values() ) ) ]
                    .sort( ( prod1: Product, prod2: Product ): number =>
                        ( prod1.name.toLowerCase() > prod2.name.toLowerCase() ) ? 1 :
                            ( ( prod1.name.toLowerCase() < prod2.name.toLowerCase() ) ? -1 : 0 ) );
                // A server refresh that landed while the cache was read is newer than it: keep that.
                if ( !serverRefreshLanded ) {
                    allProducts.value = cached;
                }

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allProducts.value.length } products in ${ t1 - t0 } ms` );
            } else {
                const seed: Product[] = markRawRows( await loadProductSeedData() );
                if ( !serverRefreshLanded ) {
                    allProducts.value = seed;
                }

                const t1: number = performance.now();
                logger.info( `Fetching & mapping ${ allProducts.value.length } product items took ${ t1 - t0 } ms` );
            }
            settled.value = true;
        } catch ( err: any ) {
            if ( err instanceof CacheClosedError ) { initLatch.halt( err ); return; }   // a newer build upgraded the cache; this tab is reloading
            logger.error( JSON.stringify( err.message ) );
            reset();
            initLatch.halt( err );   // after reset(), which clears it: a cache failure stays latched
            settled.value = true;
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    const productCatalog = computed< Product[] >(() => displayableProducts.value );

    function productById( productSysId: string ): Product | undefined {
        return productsById.get( productSysId );
    }

    function productBySlug( slug: string ): Product | undefined {
        return productsBySlug.get( slug );
    }

    /**
     * The product whose `codes` list holds this short code. Compared trimmed and case-insensitively, as
     * Permissions.isAuthorizedByShortCode does: a route's `meta.shortCode` is upper-case ('SSS') while the catalog's
     * codes are lower-case, so the 403 page could not name the product a route belongs to (UX roadmap #50).
     */
    function productByProductCode ( code: string ): Product | undefined {
        const wanted: string = `${ code ?? '' }`.trim().toLowerCase();
        if ( !wanted ) {
            return undefined;
        }

        return allProducts.value.find( ( product: Product ): boolean => {
            const productCodes = product.codes ? product.codes.split( ',' ) : [];  // Convert codes's into an array
            return productCodes.some( ( productCode: string ) => productCode.trim().toLowerCase() === wanted );
        } );
    }

    /**
     * Returns the collection of all products for which the user has an active subscription.
     *
     * @returns {ComputedRef< Product[] >} - all products with an active subscription
     */
    const subscribedProducts: ComputedRef< Product[] > = computed( () => filterProductsBySubscription( true ) );

    /**
     * Returns the collection of all products that remain after filtering out any products for which
     * the user has an active subscription.
     *
     * @returns {ComputedRef< Product[] >} - the collection of products that are available for the user to purchase
     */
    const availableProducts: ComputedRef< Product[] > = computed( () => filterProductsBySubscription( false ) );

    /**
     * Filters out non-displayable products
     *
     * @param subscribedFlag - a flag to switch between whether the collection is to contain products for which the
     * user has an active subscription
     * @returns {Product[]} - the collection of products that are either currently owned by the or the inverse of this.
     */
    function filterProductsBySubscription( subscribedFlag: boolean ): Product[] {
        let filteredProducts: Product[] = [];
        // If the product list for the non-subscribed offerings is not yet initialized & the user is logged in...
        if ( isAuthenticated.value ) {
            const products: Product[] = displayableProducts.value.filter( ( item: Product ) => {
                const itemCodes: string = item?.codes ?? '';

                let include: boolean = false;
                if ( itemCodes ) {
                    // Filter out anything that is not defined as "display-able" as well as if the user doesn't have access
                    const hasAccess: boolean = permissionsStore.hasAccessBasedOnShortCode( itemCodes );
                    include = subscribedFlag ? hasAccess : !hasAccess;
                }

                return include;
            } );

            filteredProducts = [ ...products ];
        } else if ( !subscribedFlag ) {
            // If the user is not logged in and the filter is looking for all non-subscribed products - assign all products that are display-able
            filteredProducts = displayableProducts.value;
        }

        return filteredProducts;
    }

    /**
     * Stale-while-revalidate: what the cache or seed holds stays on screen while the catalog is fetched from the server,
     * at most once until the next reset(). Concurrent and later callers share that one request. Never rejects.
     */
    function refreshOnceAsync(): Promise< void > {
        if ( !serverRefresh ) {
            serverRefresh = refreshFromServerAsync( catalogGeneration );
        }
        return serverRefresh;
    }

    async function refreshFromServerAsync( generation: number ): Promise< void > {
        refreshing.value = true;
        try {
            const fresh: Product[] = await refreshProductsFromServer();
            if ( generation !== catalogGeneration || 0 === fresh.length ) {
                return;   // a reset() started a new catalog, or the server gave nothing to replace this one with
            }
            allProducts.value = markRawRows( fresh );
            serverRefreshLanded = true;
            settled.value = true;
        } catch ( err: unknown ) {
            logger.warn( '[ Products ] refreshing the catalog from the server failed', err );
            clientStateManager.queueClientError( err, { refresh: 'products' } );
        } finally {
            if ( generation === catalogGeneration ) {
                refreshing.value = false;
            }
        }
    }

    return {
        // ACTIONS
        availableProducts,
        initializeAsync,
        productById,
        productBySlug,
        productByProductCode,
        subscribedProducts,
        reset,
        refreshOnceAsync,

        // GETTERS
        allProducts,
        displayableProducts,
        isLoading,
        isRefreshing: computed< boolean >( () => refreshing.value ),
        isSettled: computed< boolean >( () => settled.value ),
        productCatalog
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useProductsStore, import.meta.hot ) );
}
